// Templates de WhatsApp Business contra o WootDash — substitui a chamada direta
// à Graph API da Meta (com token exposto no bundle) e ao Chatwoot (contatos/labels
// do seletor de envio). O WootDash já guarda/repassa o "components" no formato
// exato da Meta (ver ComponentsJson no backend) — não inventa um formato próprio.
import { WOOTDASH_BASE, wootdashAuthHeaders } from '@/api/wootdash'
import type { WaTemplate } from '@/types/templates'

export interface CpTemplate {
  id: string
  name: string
  category: string
  language: string
  status: string
  componentsJson: string
  metaTemplateId: string | null
  rejectedReason: string | null
  variableMappingJson: string
  canEditContent: boolean
}

export interface CpContact {
  id: string
  name: string
  phone: string | null
  email: string | null
  avatarUrl: string | null
}

function authHeaders(token: string, json = false) {
  return { ...wootdashAuthHeaders(token), ...(json ? { 'Content-Type': 'application/json' } : {}) }
}

export async function listTemplates(token: string): Promise<CpTemplate[]> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/templates`, { headers: authHeaders(token) })
    if (!res.ok) return []
    return await res.json()
  } catch {
    return []
  }
}

export interface CreateOrUpdateResult { ok: boolean; failureReason?: string; detail?: string; template?: CpTemplate }

export async function createTemplate(token: string, name: string, category: string, language: string, componentsJson: string): Promise<CreateOrUpdateResult> {
  const res = await fetch(`${WOOTDASH_BASE}/templates`, {
    method: 'POST', headers: authHeaders(token, true), body: JSON.stringify({ name, category, language, componentsJson }),
  })
  const data = await res.json().catch(() => undefined)
  return res.ok ? { ok: true, template: data } : { ok: false, failureReason: data?.reason, detail: data?.detail }
}

export async function updateTemplate(token: string, id: string, category: string, language: string, componentsJson: string): Promise<CreateOrUpdateResult> {
  const res = await fetch(`${WOOTDASH_BASE}/templates/${id}`, {
    method: 'PUT', headers: authHeaders(token, true), body: JSON.stringify({ category, language, componentsJson }),
  })
  const data = await res.json().catch(() => undefined)
  return res.ok ? { ok: true, template: data } : { ok: false, failureReason: data?.reason, detail: data?.detail }
}

export async function deleteTemplate(token: string, id: string): Promise<boolean> {
  const res = await fetch(`${WOOTDASH_BASE}/templates/${id}`, { method: 'DELETE', headers: authHeaders(token) })
  return res.ok
}

export interface SyncTemplatesResult { ok: boolean; updatedCount: number; importedCount: number; templates: CpTemplate[] }

export async function syncTemplates(token: string): Promise<SyncTemplatesResult> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}/templates/sincronizar`, { method: 'POST', headers: authHeaders(token) })
    if (!res.ok) return { ok: false, updatedCount: 0, importedCount: 0, templates: [] }
    const data = await res.json()
    return { ok: true, updatedCount: data.updatedCount ?? 0, importedCount: data.importedCount ?? 0, templates: data.templates ?? [] }
  } catch {
    return { ok: false, updatedCount: 0, importedCount: 0, templates: [] }
  }
}

export async function setVariableMapping(token: string, id: string, mapping: Record<string, string>): Promise<boolean> {
  const res = await fetch(`${WOOTDASH_BASE}/templates/${id}/variaveis`, {
    method: 'PUT', headers: authHeaders(token, true), body: JSON.stringify({ mapping }),
  })
  return res.ok
}

export interface SendOutcome { contactId: string; contactName: string; success: boolean; whatsappMessageId: string | null; errorMessage: string | null }

export async function sendTemplate(token: string, id: string, contactIds: string[], manualVariableValues: Record<number, string> | null, headerImageUrl: string | null): Promise<{ ok: boolean; outcomes: SendOutcome[] }> {
  const res = await fetch(`${WOOTDASH_BASE}/templates/${id}/enviar`, {
    method: 'POST', headers: authHeaders(token, true), body: JSON.stringify({ contactIds, manualVariableValues, headerImageUrl }),
  })
  if (!res.ok) return { ok: false, outcomes: [] }
  const data = await res.json()
  return { ok: true, outcomes: data.outcomes ?? [] }
}

// O WootDash guarda components como JSON string (ComponentsJson) — o resto da tela
// (TemplatesTable, form de edição, modais) já trabalha com o array parseado, igual ao
// formato que vinha direto da Graph API antes.
export function cpTemplateToWaTemplate(t: CpTemplate): WaTemplate {
  let components: any[] = []
  try { components = JSON.parse(t.componentsJson) } catch { components = [] }
  return {
    id: t.id, name: t.name, category: t.category, language: t.language, status: t.status,
    components, variableMappingJson: t.variableMappingJson,
  }
}

export async function searchContacts(token: string, search: string, page: number, pageSize: number, tagId?: string): Promise<{ contacts: CpContact[]; total: number }> {
  try {
    const params = new URLSearchParams({ pagina: String(page), tamanhoPagina: String(pageSize) })
    if (search) params.set('busca', search)
    if (tagId) params.set('etiquetaId', tagId)
    const res = await fetch(`${WOOTDASH_BASE}/contatos?${params}`, { headers: authHeaders(token) })
    if (!res.ok) return { contacts: [], total: 0 }
    const data = await res.json()
    return { contacts: data.contacts ?? [], total: data.total ?? 0 }
  } catch {
    return { contacts: [], total: 0 }
  }
}

// CpTag/listTags viraram um serviço próprio (wootdashTags.ts) quando Etiquetas
// ganhou CRUD completo (Fase 2) — reexportado aqui só pra não quebrar quem já importava
// daqui (SendTemplateModal/TemplateContactPicker).
export type { CpTag } from './wootdashTags'
export { listTags } from './wootdashTags'
